
import {
  LayoutDashboard,
  Building2,
  Users,
  Tags,
  Settings,
  Boxes,
  Bird,
  Tally3,
  RotateCcw,
  PiggyBank,
  History,
  Egg,
  UserPlus,
  BookOpen,
  UserCog,
  FileSignature,
  Lock,
  AlertTriangle,
  UserCheck,
  Database,
  Truck,
} from "lucide-react";

export type AdminLink = {
  name: string;
  href: string;
  icon: any;
};

export type OwnerLink = AdminLink & {
  feature?: string;
  requireFeature?: boolean;
  isDropdownItem?: boolean;
  showForBusinessTypes?: string[];
  hidden?: boolean;
  description?: string; // Description pour le tutoriel
  tutorialSteps?: string[]; // Étapes du tutoriel
};

export const adminLinks: AdminLink[] = [
  { name: "Dashboard", href: "/admin", icon: LayoutDashboard },
  { name: "Entreprises", href: "/admin/companies", icon: Building2 },
  { name: "Propriétaires", href: "/admin/users", icon: Users },
  { name: "Inscrits", href: "/admin/registered-users", icon: UserCheck },
  { name: "Créer un propriétaire", href: "/admin/create-owner", icon: UserPlus },
  { name: "Types d'entreprise", href: "/admin/business-types", icon: Tags },
  { name: "Limites produits", href: "/admin/product-limits", icon: Database },
  { name: "Tutoriels", href: "/admin/tutorials", icon: BookOpen },
];

export const ownerLinks: OwnerLink[] = [
  { 
    name: "Tableau de bord", 
    href: "/owner", 
    icon: LayoutDashboard, 
    feature: "dashboard",
    description: "Vue d'ensemble de votre activité avec les statistiques principales.",
    tutorialSteps: [
      "Consultez vos ventes du jour",
      "Suivez vos objectifs de vente",
      "Visualisez vos statistiques en temps réel"
    ]
  },
  { 
    name: "Ferme avicole", 
    href: "/owner/poultry", 
    icon: Bird, 
    feature: "poultry",
    showForBusinessTypes: ["Ferme avicole"],
    requireFeature: true, // Requiert explicitement la fonctionnalité
    description: "Gestion complète de votre ferme avicole.",
    tutorialSteps: [
      "Gérez votre stock de volailles",
      "Suivez la production d'œufs",
      "Enregistrez les incidents"
    ]
  },
  { 
    name: "Stock", 
    href: "/owner/stock", 
    icon: Boxes, 
    feature: "stock",
    showForBusinessTypes: ["Commerce général", "Supermarché", "Quincaillerie"],
    description: "Gestion de votre inventaire et des produits.",
    tutorialSteps: [
      "Ajoutez de nouveaux produits",
      "Gérez les quantités en stock",
      "Configurez les prix et les unités"
    ]
  },
  { 
    name: "Vendre", 
    href: "/owner/sell", 
    icon: Tags, 
    feature: "stock",
    showForBusinessTypes: ["Commerce général", "Supermarché", "Quincaillerie", "Ferme avicole"],
    description: "Effectuez vos ventes rapidement et efficacement.",
    tutorialSteps: [
      "Sélectionnez les produits à vendre",
      "Choisissez le mode de paiement",
      "Finalisez la vente"
    ]
  },
  { 
    name: "Historique", 
    href: "/owner/history", 
    icon: History, 
    feature: "history",
    description: "Consultez l'historique complet de vos transactions.",
    tutorialSteps: [
      "Filtrez par type de transaction",
      "Consultez les détails des ventes",
      "Analysez vos données"
    ]
  },
  {
    name: "Vendue",
    href: "/owner/sold",
    icon: History,
    feature: "stock",
    showForBusinessTypes: ["Commerce général", "Supermarché", "Quincaillerie", "Ferme avicole"],
    hidden: true
  },
  { 
    name: "Comptage", 
    href: "/owner/counting", 
    icon: Tally3, 
    feature: "counting",
    description: "Gérez le comptage de votre caisse.",
    tutorialSteps: [
      "Effectuez le comptage quotidien",
      "Vérifiez les différences",
      "Validez les montants"
    ]
  },
  { 
    name: "Production d'œufs", 
    href: "/owner/egg-production", 
    icon: Egg, 
    feature: "poultry",
    showForBusinessTypes: ["Ferme avicole"],
    requireFeature: true, // Requiert explicitement la fonctionnalité
    description: "Suivez la production d'œufs de votre ferme.",
    tutorialSteps: [
      "Enregistrez la production quotidienne",
      "Suivez les tendances",
      "Analysez les performances"
    ]
  },
  { 
    name: "Fournisseurs", 
    href: "/owner/suppliers", 
    icon: Truck, 
    feature: "suppliers",
    description: "Gérez vos fournisseurs et commandes.",
    tutorialSteps: [
      "Ajoutez de nouveaux fournisseurs",
      "Suivez les commandes",
      "Gérez les documents"
    ]
  },
  { 
    name: "Incidents", 
    href: "/owner/incidents", 
    icon: AlertTriangle, 
    feature: "incidents",
    description: "Gérez les incidents et problèmes.",
    tutorialSteps: [
      "Signalez un nouvel incident",
      "Suivez le statut des incidents",
      "Résolvez les problèmes"
    ]
  },
  { 
    name: "Retours et Crédits", 
    href: "/owner/returns", 
    icon: RotateCcw, 
    feature: "returns",
    description: "Gérez les retours produits et les crédits clients.",
    tutorialSteps: [
      "Traitez les retours",
      "Gérez les crédits clients",
      "Suivez les remboursements"
    ]
  },
  { 
    name: "Contrat", 
    href: "/owner/employee-contract", 
    icon: FileSignature, 
    feature: "contracts",
    description: "Gérez les contrats de vos employés.",
    tutorialSteps: [
      "Créez un nouveau contrat",
      "Gérez les contrats existants",
      "Suivez les échéances"
    ]
  },
  { 
    name: "Accès propriétaire", 
    href: "#", 
    icon: Lock, 
    feature: "owner_access",
    description: "Gérez les accès et les permissions.",
    tutorialSteps: [
      "Configurez les accès",
      "Gérez les permissions",
      "Sécurisez votre compte"
    ]
  },
  { 
    name: "Personnel", 
    href: "/owner/staff", 
    icon: UserCog, 
    feature: "staff",
    isDropdownItem: true,
    description: "Gérez votre équipe et les employés.",
    tutorialSteps: [
      "Ajoutez de nouveaux employés",
      "Gérez les rôles",
      "Suivez les performances"
    ]
  },
  { 
    name: "Budget", 
    href: "/owner/budget", 
    icon: PiggyBank, 
    feature: "budget",
    isDropdownItem: true,
    description: "Gérez votre budget et vos finances.",
    tutorialSteps: [
      "Consultez vos revenus",
      "Gérez vos dépenses",
      "Analysez votre rentabilité"
    ]
  },
  { 
    name: "Paramètres", 
    href: "/owner/settings", 
    icon: Settings, 
    feature: "settings",
    isDropdownItem: true,
    description: "Configurez votre compte et vos préférences.",
    tutorialSteps: [
      "Personnalisez votre profil",
      "Configurez les notifications",
      "Gérez vos préférences"
    ]
  },
];
